import { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useKeycloak } from "@react-keycloak/web";
import ProjectService from '../services/Projects/ProjectService';

const useBreadcrumb = () => {
    const location = useLocation();
    const params = useParams();
    const { keycloak } = useKeycloak();
    const [breadcrumbItems, setBreadcrumbItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const routeLabels = {
        '/': 'Home',
        '/home': 'Home',
        '/projects': 'Projetos',
        '/new-project': 'Novo Projeto',
        '/summary-project': 'Resumo do Projeto',
        '/review-project': 'Revisar Projeto',
        '/profile': 'Perfil',
        '/brainstorm': 'Brainstorm',
        '/admin': 'Administração',
        '/dashboard': 'Dashboard',
        '/history-chats': 'Histórico de Conversas',
        '/locked': 'Bloqueado'
    };

    const generateBreadcrumbs = async () => {
        const path = location.pathname;
        const breadcrumbs = [];

        const userRoles = keycloak.tokenParsed?.realm_access.roles || [];
        const isProcessUser = userRoles.includes('mjv-user');
        const fetchProject = (projectId) => isProcessUser
            ? ProjectService.getProjectById(projectId, keycloak.token)
            : ProjectService.getProjectByIdUserRole(projectId, keycloak.token);
        
        breadcrumbs.push({
            label: 'Home',
            path: isProcessUser ? '/projects' : '/',
            isActive: false
        });

        if (path === '/' || path === '/home') {
            breadcrumbs[0].isActive = true;
        }
        else if (path === '/projects') {
            breadcrumbs.push({
                label: 'Projetos',
                path: '/projects',
                isActive: true
            });
        }
        else if (path.startsWith('/project/') && params.id) {
            breadcrumbs.push({
                label: 'Projetos',
                path: '/projects',
                isActive: false
            });

            try {
                setLoading(true);
                const project = await fetchProject(params.id);
                breadcrumbs.push({
                    label: project?.name || `Projeto ${params.id}`,
                    path: `/project/${params.id}`,
                    isActive: true
                });
            } catch (error) {
                breadcrumbs.push({
                    label: `Projeto ${params.id}`,
                    path: `/project/${params.id}`,
                    isActive: true
                });
            } finally {
                setLoading(false);
            }
        }
        else if (path.startsWith('/edit-project/') && params.id) {
            breadcrumbs.push({
                label: 'Projetos',
                path: '/projects',
                isActive: false
            });

            try {
                setLoading(true);
                const project = await fetchProject(params.id);
                breadcrumbs.push({
                    label: project?.name || `Projeto ${params.id}`,
                    path: `/project/${params.id}`,
                    isActive: false
                });
                breadcrumbs.push({
                    label: 'Editar',
                    path: `/edit-project/${params.id}`,
                    isActive: true
                });
            } catch (error) {
                breadcrumbs.push({
                    label: `Projeto ${params.id}`,
                    path: `/project/${params.id}`,
                    isActive: false
                });
                breadcrumbs.push({
                    label: 'Editar',
                    path: `/edit-project/${params.id}`,
                    isActive: true
                });
            } finally {
                setLoading(false);
            }
        }
        else if (path.startsWith('/brainstorm/') && params.projectId) {
            breadcrumbs.push({
                label: 'Projetos',
                path: '/projects',
                isActive: false
            });

            try {
                setLoading(true);
                const project = await fetchProject(params.projectId);
                breadcrumbs.push({
                    label: project?.name || `Projeto ${params.projectId}`,
                    path: `/project/${params.projectId}`,
                    isActive: false
                });
                breadcrumbs.push({
                    label: 'Brainstorm',
                    path: `/brainstorm/${params.projectId}`,
                    isActive: true
                });
            } catch (error) {
                breadcrumbs.push({
                    label: `Projeto ${params.projectId}`,
                    path: `/project/${params.projectId}`,
                    isActive: false
                });
                breadcrumbs.push({
                    label: 'Brainstorm',
                    path: `/brainstorm/${params.projectId}`,
                    isActive: true
                });
            } finally {
                setLoading(false);
            }
        }
        else if (path.startsWith('/chat/') && params.projectId) {
            breadcrumbs.push({
                label: 'Conversas',
                path: '/history-chats',
                isActive: false
            });

            try {
                setLoading(true);
                const project = await fetchProject(params.projectId);
                const projectLabel = `Projeto ${project?.name || params.projectId}`;

                if (params.chatId) {
                    breadcrumbs.push({
                        label: projectLabel,
                        path: `/chat/${params.projectId}`,
                        isActive: false
                    });
                    breadcrumbs.push({
                        label: `Chat ${params.chatId}`,
                        path: `/chat/${params.projectId}/${params.chatId}`,
                        isActive: true
                    });
                } else {
                    breadcrumbs.push({
                        label: projectLabel,
                        path: `/chat/${params.projectId}`,
                        isActive: true
                    });
                }
            } catch (error) {
                const fallbackLabel = `Projeto ${params.projectId}`;

                if (params.chatId) {
                    breadcrumbs.push({
                        label: fallbackLabel,
                        path: `/chat/${params.projectId}`,
                        isActive: false
                    });
                    breadcrumbs.push({
                        label: `Chat ${params.chatId}`,
                        path: `/chat/${params.projectId}/${params.chatId}`,
                        isActive: true
                    });
                } else {
                    breadcrumbs.push({
                        label: fallbackLabel,
                        path: `/chat/${params.projectId}`,
                        isActive: true
                    });
                }
            } finally {
                setLoading(false);
            }
        }
        else {
            const routeLabel = routeLabels[path];
            if (routeLabel) {
                breadcrumbs.push({
                    label: routeLabel,
                    path: path,
                    isActive: true
                });
            }
        }

        setBreadcrumbItems(breadcrumbs);
    };

    useEffect(() => {
        if (keycloak.token) {
            generateBreadcrumbs();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, params, keycloak.token]);

    return { breadcrumbItems, loading };
};

export default useBreadcrumb;

